/**
 * pages/ResetPassword.tsx
 * Route: /reset-password?token=xxx
 * User lands here from the password reset email link.
 * Reads ?token from URL, lets user set new password.
 * Awaits: POST /api/auth/reset-password (backend already scaffolded)
 */

import React, { useState } from 'react';
import { Eye, EyeOff, Check, AlertCircle, Loader, ArrowLeft, Lock } from 'lucide-react';
import { authApi } from '../api/client';

const C = {
  amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A',
  ink:'#1A1814', inkLight:'#3D3A34', muted:'#7A7570',
  border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF',
  red:'#DC2626', redBg:'#FEF2F2', green:'#16A34A', greenBg:'#F0FDF4',
};
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

// Password strength helper
function strength(p: string) {
  let s = 0;
  if (p.length >= 8)          s++;
  if (/[A-Z]/.test(p))        s++;
  if (/[0-9]/.test(p))        s++;
  if (/[^A-Za-z0-9]/.test(p)) s++;
  return s;
}
const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'];
const strengthColor = ['', '#DC2626', '#F59E0B', '#F5A623', '#16A34A'];

const ResetPassword: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const token  = params.get('token') || '';

  const [password, setPassword]   = useState('');
  const [confirm,  setConfirm]    = useState('');
  const [showPw,   setShowPw]     = useState(false);
  const [showCf,   setShowCf]     = useState(false);
  const [loading,  setLoading]    = useState(false);
  const [done,     setDone]       = useState(false);
  const [error,    setError]      = useState('');

  const s = strength(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8)    { setError('Password must be at least 8 characters.'); return; }
    if (s < 2)                   { setError('Please choose a stronger password.'); return; }
    if (password !== confirm)    { setError('Passwords do not match.'); return; }
    if (!token)                  { setError('Invalid or missing reset token. Please request a new link.'); return; }

    setLoading(true);
    try {
      await authApi.resetPassword(token, password);
      setDone(true);
    } catch (err: any) {
      setError(err?.message || 'This reset link has expired. Please request a new one.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'DM Sans', sans-serif; background: #FAFAF8; }
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes fadeUp  { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }

        .rp-card {
          width: 100%;
          max-width: 440px;
          animation: fadeUp .4s ease both;
        }
        .rp-input-wrap { position: relative; }
        .rp-input {
          width: 100%;
          height: 48px;
          padding: 0 46px 0 16px;
          border: 1.5px solid #E8E4DC;
          border-radius: 10px;
          font-size: 15px;
          font-family: 'DM Sans', sans-serif;
          color: #1A1814;
          background: #fff;
          outline: none;
          transition: border-color .15s, box-shadow .15s;
        }
        .rp-input:focus {
          border-color: #F5A623;
          box-shadow: 0 0 0 3px #F5A62322;
        }
        .rp-toggle {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: #7A7570;
          display: flex;
          padding: 2px;
        }
        .rp-submit {
          width: 100%;
          height: 50px;
          border-radius: 12px;
          border: none;
          cursor: pointer;
          background: linear-gradient(135deg, #F5A623, #C4811A);
          color: #fff;
          font-weight: 700;
          font-size: 16px;
          font-family: 'DM Sans', sans-serif;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          box-shadow: 0 6px 20px #F5A62344;
          transition: opacity .15s;
        }
        .rp-submit:disabled { opacity: 0.75; cursor: not-allowed; }

        @media (max-width: 480px) {
          .rp-page { padding: 32px 16px 40px; }
          .rp-card { max-width: 100%; }
        }
      `}</style>

      <div className="rp-page" style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'40px 24px', background:`linear-gradient(160deg,#FFFDF8 60%,#FFF6E0 100%)` }}>

        {/* Logo */}
        <a href="/" style={{ display:'flex', alignItems:'center', gap:10, textDecoration:'none', marginBottom:40 }}>
          <div style={{ width:36, height:36, borderRadius:11, background:`linear-gradient(145deg,${C.amber200},${C.amber400})`, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <svg width="18" height="18" viewBox="0 0 180 180" fill="none">
              <g transform="translate(90,88) rotate(-35)"><rect x="-12" y="-38" width="24" height="58" rx="7" fill="white"/><rect x="-12" y="-38" width="24" height="16" rx="7" fill="#F08030" opacity="0.9"/><polygon points="-12,20 12,20 0,44" fill="white" opacity="0.9"/></g>
              <circle cx="104" cy="102" r="42" fill="none" stroke="white" strokeWidth="13" opacity="0.95"/>
              <g transform="translate(126,124) rotate(45)"><rect x="-6" y="-4" width="30" height="10" rx="5" fill="white"/></g>
            </svg>
          </div>
          <span style={{ fontFamily:font.head, fontWeight:800, fontSize:22, letterSpacing:'-0.05em', color:C.ink }}>Jötr</span>
        </a>

        <div className="rp-card">
          <a href="/login" style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13, color:C.muted, textDecoration:'none', marginBottom:32, fontFamily:font.body, fontWeight:500 }}>
            <ArrowLeft size={14}/> Back to login
          </a>

          <div style={{ background:C.white, borderRadius:20, border:`1px solid ${C.border}`, padding:'36px', boxShadow:'0 8px 32px rgba(0,0,0,0.06)' }}>

            {!token ? (
              /* Invalid / missing token */
              <div style={{ textAlign:'center' }}>
                <div style={{ width:56, height:56, borderRadius:16, background:C.redBg, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
                  <AlertCircle size={24} color={C.red}/>
                </div>
                <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:22, color:C.ink, marginBottom:10 }}>Invalid reset link</h1>
                <p style={{ fontSize:14, color:C.muted, fontFamily:font.body, lineHeight:1.7, marginBottom:24 }}>
                  This password reset link is invalid or has expired. Reset links are valid for 30 minutes.
                </p>
                <a href="/forgot-password" style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'11px 24px', borderRadius:10, background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:14, textDecoration:'none', fontFamily:font.body }}>
                  Request a new link
                </a>
              </div>
            ) : done ? (
              /* Success */
              <div style={{ textAlign:'center' }}>
                <div style={{ width:60, height:60, borderRadius:'50%', background:C.greenBg, border:`2px solid ${C.green}44`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
                  <Check size={26} color={C.green}/>
                </div>
                <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:22, color:C.ink, marginBottom:10 }}>Password updated!</h1>
                <p style={{ fontSize:14, color:C.muted, fontFamily:font.body, lineHeight:1.7, marginBottom:28 }}>
                  Your password has been changed successfully. You can now log in with your new password.
                </p>
                <a href="/login" style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'12px 28px', borderRadius:11, background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:15, textDecoration:'none', fontFamily:font.body }}>
                  Log in to Jötr
                </a>
              </div>
            ) : (
              /* Form */
              <>
                <div style={{ width:48, height:48, borderRadius:15, background:C.amber100, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:20 }}>
                  <Lock size={22} color={C.amber400}/>
                </div>
                <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:26, letterSpacing:'-0.04em', color:C.ink, marginBottom:8 }}>
                  Set new password
                </h1>
                <p style={{ fontSize:14, color:C.muted, fontFamily:font.body, lineHeight:1.6, marginBottom:28 }}>
                  Choose a strong password for your Jötr account.
                </p>

                <form onSubmit={handleSubmit} noValidate style={{ display:'flex', flexDirection:'column', gap:18 }}>

                  {/* New password */}
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    <label style={{ fontSize:13, fontWeight:600, color:C.inkLight, fontFamily:font.body }}>New password</label>
                    <div className="rp-input-wrap">
                      <input className="rp-input" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters" autoFocus/>
                      <button type="button" className="rp-toggle" onClick={() => setShowPw(v => !v)}>
                        {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                      </button>
                    </div>
                    {/* Strength bar */}
                    {password && (
                      <div>
                        <div style={{ display:'flex', gap:4, marginBottom:5 }}>
                          {[1,2,3,4].map(i => (
                            <div key={i} style={{ flex:1, height:3, borderRadius:2, background: i <= s ? strengthColor[s] : C.border, transition:'background .2s' }}/>
                          ))}
                        </div>
                        <span style={{ fontSize:11, color:strengthColor[s], fontFamily:font.body, fontWeight:600 }}>{strengthLabel[s]}</span>
                      </div>
                    )}
                  </div>

                  {/* Confirm password */}
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    <label style={{ fontSize:13, fontWeight:600, color:C.inkLight, fontFamily:font.body }}>Confirm password</label>
                    <div className="rp-input-wrap">
                      <input className="rp-input" type={showCf ? 'text' : 'password'} value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat your password"/>
                      <button type="button" className="rp-toggle" onClick={() => setShowCf(v => !v)}>
                        {showCf ? <EyeOff size={16}/> : <Eye size={16}/>}
                      </button>
                    </div>
                    {/* Match indicator */}
                    {confirm && (
                      <span style={{ fontSize:12, fontWeight:600, fontFamily:font.body, color: password === confirm ? C.green : C.red }}>
                        {password === confirm ? '✓ Passwords match' : '✗ Passwords do not match'}
                      </span>
                    )}
                  </div>

                  {/* Error */}
                  {error && (
                    <div style={{ display:'flex', alignItems:'flex-start', gap:8, padding:'12px 14px', borderRadius:9, background:C.redBg, border:`1px solid ${C.red}33` }}>
                      <AlertCircle size={15} color={C.red} style={{ flexShrink:0, marginTop:1 }}/>
                      <span style={{ fontSize:13, color:C.red, fontFamily:font.body, lineHeight:1.5 }}>{error}</span>
                    </div>
                  )}

                  <button type="submit" disabled={loading} className="rp-submit">
                    {loading
                      ? <><Loader size={17} style={{ animation:'spin .7s linear infinite' }}/> Updating…</>
                      : <><Check size={17}/> Set new password</>}
                  </button>
                </form>
              </>
            )}
          </div>

          <p style={{ textAlign:'center', fontSize:12, color:C.muted, marginTop:20, fontFamily:font.body }}>
            Remember your password?{' '}
            <a href="/login" style={{ color:C.amber400, fontWeight:600, textDecoration:'none' }}>Log in</a>
          </p>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;